import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useColdCallAuth } from '../context/ColdCallAuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { 
  Phone, 
  Play, 
  StopCircle,
  Mic,
  MicOff,
  Clock,
  Award,
  TrendingUp,
  Target,
  Users,
  ChevronRight,
  LogOut,
  History,
  Trophy,
  Loader2,
  X,
  CheckCircle,
  AlertCircle,
  Volume2,
  PhoneCall,
  PhoneOff,
  MessageSquare,
  BarChart3,
  BookOpen,
  FileText,
  UserCheck,
  Shield,
  Lightbulb,
  ChevronDown,
  ChevronUp,
  Briefcase,
  Star,
  ArrowRight
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/coldcall`;

// ==================== PREPARATION CENTER DATA ====================

const CALL_SCRIPTS = {
  cold_intro: {
    title: "Cold Introduction Script",
    description: "First call - no prior contact",
    template: `**Opening (5-10 seconds)**
"Hi [Name], this is [Your Name] from [Company]. I know I'm calling out of the blue - do you have 30 seconds?"

**Hook (10-15 seconds)**
"The reason I'm calling is [specific trigger/research]. Companies like [similar company] are using [your solution] to [specific outcome with numbers]."

**Qualify (15-20 seconds)**
"I'm curious - how are you currently handling [pain point]?"

**Bridge to Next Step**
"Based on what you've shared, I think a quick 15-minute call would be valuable. Would [day/time] work, or is [alternative] better?"`,
    tips: [
      "Research the prospect before calling - check LinkedIn, company news",
      "Lead with curiosity, not pitch",
      "Get permission to continue within first 10 seconds",
      "Have a specific reason for calling (trigger event)"
    ]
  },
  followup: {
    title: "Follow-up Call Script",
    description: "Following up on email/LinkedIn",
    template: `**Opening**
"Hi [Name], this is [Your Name] from [Company]. I sent you an email last [day] about [specific topic] - did you get a chance to look at it?"

**If Yes:**
"Great! What stood out to you?" → Listen and address

**If No/Busy:**
"No worries - the quick summary is [one-sentence value prop]. The reason I reached out is [specific trigger]. Does that resonate with what you're dealing with?"

**Close**
"Would it make sense to set up 15 minutes to explore this further? I'm free [specific times]."`,
    tips: [
      "Reference your previous touchpoint specifically",
      "Don't be apologetic for following up",
      "Have new value to add (case study, insight, news)",
      "Follow up 2-3 times before moving on"
    ]
  },
  discovery: {
    title: "Discovery Call Script",
    description: "Understanding needs and pain points",
    template: `**Agenda Setting**
"Thanks for taking the time. I'd love to learn more about [their situation] and see if there's a fit. Can I ask a few questions?"

**Situation Questions**
- "Tell me about your current [process/tool/approach]?"
- "How long have you been doing it this way?"
- "What's working well? What's not?"

**Problem Questions**
- "What happens when [pain point occurs]?"
- "How does that impact [team/revenue/customers]?"
- "What have you tried to solve this?"

**Implication Questions**
- "If this continues, what's the cost to [business metric]?"
- "How does this affect your [goals/KPIs]?"

**Need-Payoff Questions**
- "If you could [solve problem], what would that mean for [outcome]?"
- "What would success look like?"`,
    tips: [
      "Listen more than you talk (aim for 70/30)",
      "Take notes and repeat back what you hear",
      "Dig deeper with 'Tell me more about that'",
      "Connect their problems to your solution naturally"
    ]
  },
  objection: {
    title: "Objection Handling Framework",
    description: "Dealing with common pushbacks",
    template: `**The LAER Framework:**
- **L**isten - Let them finish, don't interrupt
- **A**cknowledge - "I understand" / "That makes sense"
- **E**xplore - "Help me understand..." / "What specifically concerns you?"
- **R**espond - Address with proof/story/reframe

**Common Objections & Responses:**

**"We don't have budget"**
→ "I hear you. Budget is always a consideration. Out of curiosity, if budget wasn't a factor, would this be something you'd want to explore? What I'm hearing is [restate value]. Would it help if I showed you how [Company X] got ROI within [timeframe]?"

**"We're happy with our current solution"**
→ "That's great to hear - what do you like most about it? [Listen] Many of our customers said the same thing before switching. They found [specific differentiator]. Would you be open to seeing how we compare?"

**"Send me an email"**
→ "Absolutely, I'll do that. To make sure I send relevant info - what's your biggest priority around [area] right now? That way I can include a case study that's actually useful."

**"I need to think about it"**
→ "Of course. What specifically would you want to think through? Is it [price/fit/timing]? I ask because I might be able to help clarify right now."`,
    tips: [
      "Objections often mean they're interested but have concerns",
      "Never argue - acknowledge and explore",
      "Have case studies ready for each common objection",
      "Ask 'What would need to be true for this to make sense?'"
    ]
  },
  referral: {
    title: "Referral Introduction Script",
    description: "Warm intro from mutual connection",
    template: `**Opening**
"Hi [Name], this is [Your Name] from [Company]. [Mutual Connection] suggested I reach out to you - they mentioned you're [specific context]."

**Build Credibility**
"I've been working with [Mutual Connection] on [project/outcome], and they thought you might be facing similar challenges around [pain point]."

**Transition**
"Is that something you're dealing with? I'd love to hear more about your situation."

**If Yes:**
"Great. [Mutual Connection] saw results like [specific outcome]. Would it make sense to set up a quick call to see if we could do something similar for you?"

**If Not a Priority:**
"I appreciate you sharing that. Is there someone else on your team who might be dealing with [related challenge]? Or would it be okay if I checked back in [timeframe]?"`,
    tips: [
      "Always mention the referrer's name immediately",
      "Ask the referrer for specific context before calling",
      "Share a quick win you had with the referrer",
      "Offer value even if timing isn't right"
    ]
  },
  demo_booking: {
    title: "Demo Booking Script",
    description: "Converting interest to meeting",
    template: `**Confirm Interest**
"Based on our conversation, it sounds like [summarize their pain/goal]. Is that accurate?"

**Set Expectations**
"In our demo, I'll show you exactly how we [address their specific pain]. It typically takes 20-30 minutes. You'll see [specific features] and how companies like [similar company] are using it to [outcome]."

**Create Urgency (if appropriate)**
"I should mention - we're [launching feature/running promotion/limited capacity]. Would it make sense to get this scheduled this week?"

**Offer Choices**
"I have availability [Day 1] at [Time] or [Day 2] at [Time]. Which works better for you?"

**Confirm & Add Value**
"Perfect, I'll send over a calendar invite. Before then, I'll also send you a quick case study on [relevant company] so you can see results upfront."`,
    tips: [
      "Always offer two specific time options",
      "Summarize their pain points before booking",
      "Set clear expectations for the demo",
      "Send a confirmation with agenda within 5 minutes"
    ]
  }
};

const PERSONA_GUIDES = {
  skeptical_ceo: {
    name: "Skeptical CEO",
    difficulty: "Hard",
    whatTheyCareAbout: [
      "ROI and bottom-line impact",
      "Proof and case studies from similar companies",
      "Their time - they have 2 minutes max",
      "Strategic value, not features"
    ],
    howTheyRespond: [
      "Dismissive initially - 'I have 2 minutes'",
      "Ask for proof immediately",
      "Push back on vague claims",
      "Want specific numbers and references"
    ],
    howToWinThem: [
      "Lead with a strong, specific hook",
      "Have 2-3 reference customers ready",
      "Be concise - every word matters",
      "Earn the right to their time before asking for more"
    ],
    commonObjections: [
      "We've tried something like this before",
      "Send me an email",
      "I'm not the right person",
      "What proof do you have?"
    ]
  },
  busy_vp: {
    name: "Busy VP Sales",
    difficulty: "Medium",
    whatTheyCareAbout: [
      "How it helps them hit quota",
      "Competitive advantage",
      "Speed of implementation",
      "Team adoption ease"
    ],
    howTheyRespond: [
      "Rushed but engaged if you catch interest",
      "Ask about competitors",
      "Want to know what's different",
      "Results-focused questions"
    ],
    howToWinThem: [
      "Speak their language - revenue, pipeline, quota",
      "Mention competitor wins",
      "Show fast time-to-value",
      "Offer to include their team in evaluation"
    ],
    commonObjections: [
      "How is this different from [competitor]?",
      "We're locked into a contract",
      "Talk to my team first",
      "We're in the middle of quarter"
    ]
  },
  curious_manager: {
    name: "Curious Manager",
    difficulty: "Easy",
    whatTheyCareAbout: [
      "Understanding how it works",
      "How it makes their job easier",
      "Getting buy-in from leadership",
      "Not making a wrong decision"
    ],
    howTheyRespond: [
      "Open and friendly",
      "Ask lots of questions",
      "Need to check with their VP",
      "Thoughtful and evaluative"
    ],
    howToWinThem: [
      "Be patient and educational",
      "Help them build internal case",
      "Provide materials they can share up",
      "Offer to join call with their leadership"
    ],
    commonObjections: [
      "I'd need to run this by my VP",
      "Can you send more information?",
      "What's the implementation like?",
      "This sounds interesting but..."
    ]
  },
  gatekeeper: {
    name: "Executive Assistant",
    difficulty: "Medium",
    whatTheyCareAbout: [
      "Protecting their executive's time",
      "Not letting through time-wasters",
      "Being helpful and professional",
      "Following proper procedures"
    ],
    howTheyRespond: [
      "Professional and procedural",
      "Ask qualifying questions",
      "Protective of executive access",
      "Will take messages if appropriate"
    ],
    howToWinThem: [
      "Be respectful and professional",
      "Have a clear, concise reason for calling",
      "Ask for their help, not just to get through",
      "Build rapport - they're influential"
    ],
    commonObjections: [
      "What is this regarding?",
      "Are they expecting your call?",
      "They're in meetings all day",
      "Can I take a message?"
    ]
  },
  technical_buyer: {
    name: "Technical Buyer",
    difficulty: "Hard",
    whatTheyCareAbout: [
      "Technical specifications",
      "Security and compliance",
      "Integration capabilities",
      "Implementation requirements"
    ],
    howTheyRespond: [
      "Analytical and detailed",
      "Ask technical questions",
      "Skeptical of marketing claims",
      "Want to see documentation"
    ],
    howToWinThem: [
      "Know your technical specs",
      "Have documentation ready",
      "Be honest about limitations",
      "Offer technical deep-dive"
    ],
    commonObjections: [
      "What's your uptime/SLA?",
      "How does integration work?",
      "What about security/compliance?",
      "What's the implementation timeline?"
    ]
  },
  friendly_founder: {
    name: "Friendly Founder",
    difficulty: "Easy",
    whatTheyCareAbout: [
      "Vision and innovation",
      "Moving fast",
      "Culture fit",
      "Solving real problems"
    ],
    howTheyRespond: [
      "Enthusiastic and open",
      "May go off on tangents",
      "Share their own challenges",
      "Easy to build rapport with"
    ],
    howToWinThem: [
      "Match their energy",
      "Be authentic and direct",
      "Show you understand startups",
      "Keep them focused on next steps"
    ],
    commonObjections: [
      "We're still figuring things out",
      "Let me tell you about our situation...",
      "That's interesting! We also...",
      "Can you be flexible on pricing?"
    ]
  }
};

const BEST_PRACTICES = [
  {
    title: "Before the Call",
    icon: "clipboard",
    tips: [
      "Research the prospect: LinkedIn, company news, recent announcements",
      "Prepare your opening hook based on their specific situation",
      "Have 2-3 relevant case studies ready",
      "Know your objective: What's the ideal outcome?"
    ]
  },
  {
    title: "Opening Strong",
    icon: "target",
    tips: [
      "State your name and company clearly",
      "Get permission to continue in first 10 seconds",
      "Lead with value, not features",
      "Reference something specific about them"
    ]
  },
  {
    title: "Building Rapport",
    icon: "handshake",
    tips: [
      "Listen more than you talk (70/30 rule)",
      "Mirror their communication style",
      "Use their name naturally",
      "Find common ground"
    ]
  },
  {
    title: "Handling Objections",
    icon: "shield",
    tips: [
      "Never argue or get defensive",
      "Acknowledge before responding",
      "Ask questions to understand the real concern",
      "Have stories and proof ready"
    ]
  },
  {
    title: "Closing Strong",
    icon: "trophy",
    tips: [
      "Always propose a specific next step",
      "Offer two time options, not open-ended",
      "Summarize value before asking",
      "Send calendar invite within 5 minutes"
    ]
  },
  {
    title: "After the Call",
    icon: "notepad",
    tips: [
      "Send follow-up email within 24 hours",
      "Document what you learned in CRM",
      "Plan your next touchpoint",
      "Review what worked and what didn't"
    ]
  }
];

// ==================== INTERVIEW PREP CENTER DATA ====================

const INTERVIEW_SCRIPTS = {
  cold_outreach: {
    title: "Cold Outreach for a Job",
    description: "Reaching out cold when you have no prior connection",
    template: `**Opening (5-10 seconds)**
"Hi [Name], this is [Your Name]. I know I'm calling unexpectedly - do you have just 30 seconds? I'll be quick."

**Hook (10-15 seconds)**
"I've been following [Company]'s work on [specific project/news/product], and I'm really impressed by [specific detail]. I'm a [your role/specialty] with experience in [relevant skill], and I'd love to explore if there's a way I could contribute to your team."

**Elevator Pitch (15-20 seconds)**
"In my last role at [Company], I [specific achievement with numbers - e.g., 'helped increase conversion rates by 35%' or 'led a team of 8 engineers to ship 3 major features']. I specialize in [key skills relevant to their company]."

**Ask (Direct & Respectful)**
"I know you're busy, so I'll be direct - are there any openings on your team, or could you point me to the right person to speak with? Even a 10-minute conversation would be incredibly valuable."

**If No Openings:**
"I totally understand. Would it be okay if I sent you my resume for future reference? And is there anyone else in the organization you'd suggest I connect with?"`,
    tips: [
      "Research the company deeply - mention something specific and recent",
      "Lead with what you can do for THEM, not what you want",
      "Keep your pitch under 30 seconds - practice timing it",
      "Have a backup ask ready (informational chat, referral, resume review)",
      "Sound confident but not arrogant - enthusiasm is your superpower"
    ]
  },
  post_application: {
    title: "Follow-up After Applying Online",
    description: "You submitted an application and want to follow up",
    template: `**Opening**
"Hi [Name], this is [Your Name]. I recently applied for the [Role Title] position through your careers page, and I wanted to follow up personally to express my genuine interest."

**Why You:**
"I applied because [specific reason tied to the company/role]. In my previous role, I [relevant accomplishment that directly relates to the job requirements]."

**Show Initiative:**
"I took the time to [research/prepare something relevant - e.g., 'review your latest product release' or 'read your team's recent blog posts'], and I have some ideas about how I could contribute to [specific area]."

**The Ask:**
"I'd love the opportunity to discuss how my experience aligns with what you're looking for. Would you have 10-15 minutes this week for a brief conversation?"

**If They're Not the Right Person:**
"I appreciate your time. Could you point me to the hiring manager for this role? I'd love to connect with them directly."`,
    tips: [
      "Follow up 3-5 business days after applying",
      "Reference the specific job title and when you applied",
      "Show you've done more research beyond the job posting",
      "Be persistent but respectful - one follow-up call is fine",
      "Always have the job ID/title ready if asked"
    ]
  },
  referral_outreach: {
    title: "Referral-Based Outreach",
    description: "Someone recommended you reach out to this person",
    template: `**Opening (Mention referrer immediately)**
"Hi [Name], this is [Your Name]. [Referrer's Name] suggested I reach out to you - they mentioned you might be the right person to talk to about opportunities on your team."

**Build Credibility Through Referrer:**
"[Referrer] and I worked together at [context], and they spoke very highly of [Company] and your team specifically. They thought my background in [skill/area] could be a good fit."

**Your Pitch:**
"I have [X years] of experience in [relevant area]. Most recently, I [key achievement]. [Referrer] mentioned your team is working on [specific project/challenge], and that's exactly the kind of work I'm passionate about."

**The Ask:**
"Would you be open to a brief conversation? I'd love to learn more about what you're building and see if there might be a mutual fit."

**If Timing is Off:**
"I completely understand. Would it be alright if I sent you my LinkedIn profile and checked back in a few weeks?"`,
    tips: [
      "ALWAYS mention the referrer's name in the first sentence",
      "Ask the referrer for context before calling (team challenges, culture, etc.)",
      "Don't oversell the referral - 'suggested I reach out' is perfect",
      "Thank the referrer afterwards regardless of outcome",
      "Send a follow-up email within an hour referencing the conversation"
    ]
  },
  linkedin_followup: {
    title: "LinkedIn Follow-up Call",
    description: "Following up after connecting on LinkedIn",
    template: `**Opening**
"Hi [Name], this is [Your Name]. We connected on LinkedIn recently, and I really appreciated you accepting my request. I wanted to follow up with a quick call if you have a moment."

**Reference Your LinkedIn Interaction:**
"I noticed [something from their profile/posts - e.g., 'your post about scaling engineering teams' or 'the impressive growth at Company']. It really resonated with my own experience in [relevant area]."

**Bridge to Opportunity:**
"I'm currently exploring new opportunities in [field/role type], and your team at [Company] caught my attention because [specific reason]. I'd love to learn more about what you're working on."

**The Ask:**
"Would you be open to a 10-minute chat? I'm happy to work around your schedule. Even an informational conversation would be really valuable for me."

**If They're Busy:**
"No worries at all. I'll send you a message on LinkedIn with a bit more about my background. If anything catches your eye, I'd love to continue the conversation."`,
    tips: [
      "Engage with their content BEFORE calling (like, comment, share)",
      "Reference specific posts or achievements from their profile",
      "Don't call the same day you connect - wait 2-3 days",
      "Have your LinkedIn profile polished before making the call",
      "Frame it as wanting to learn, not just asking for a job"
    ]
  },
  informational: {
    title: "Informational Interview Request",
    description: "Seeking advice and learning about opportunities",
    template: `**Opening (Low pressure)**
"Hi [Name], this is [Your Name]. I'm not calling with a sales pitch - I'm actually a [your field] professional who's been really impressed by [Company]'s work in [area]. Would you have a few minutes?"

**Show Genuine Interest:**
"I've been researching companies that are [specific thing the company does well], and [Company] keeps coming up. I'm particularly interested in [specific aspect of their work]."

**The Ask (Frame as Learning):**
"I'd love to pick your brain for 15 minutes about [specific topic - e.g., 'what it's like working on the data team' or 'how the company approaches product development']. Your perspective would be incredibly valuable as I'm navigating my career in this space."

**Be Transparent:**
"I want to be upfront - I'm actively exploring opportunities, and I'm trying to make informed decisions about where I can make the biggest impact. This is primarily about learning, not asking you for a job."

**Close:**
"Would a brief coffee chat or phone call work sometime this week or next? I promise to respect your time."`,
    tips: [
      "Informational interviews are about learning, not pitching yourself",
      "Prepare 5-7 thoughtful questions that show you've done research",
      "NEVER ask for a job directly during an informational interview",
      "Send a thank-you note within 24 hours",
      "Follow up with any articles/resources relevant to what they shared"
    ]
  },
  event_followup: {
    title: "Event / Conference Follow-up",
    description: "Following up after meeting at an event",
    template: `**Opening (Reference the Event)**
"Hi [Name], this is [Your Name]. We met briefly at [Event Name] last [day/week] - we talked about [specific topic you discussed]. I hope you enjoyed the rest of the event!"

**Recall the Connection:**
"I really enjoyed our conversation about [topic]. You mentioned [something specific they said], and it got me thinking about [how it relates to your skills/interests]."

**Bridge to Opportunity:**
"Since we talked, I've been doing more research on [Company/their team], and I'm even more excited about the work you're doing in [area]. I'd love to continue our conversation."

**The Ask:**
"Would you be open to grabbing a coffee or hopping on a quick call this week? I'd love to learn more about [specific topic] and explore if there might be a way I could contribute."

**If They Don't Remember You:**
"No worries! There were so many great people there. I was the one who [distinguishing detail]. I'd still love to connect if you're open to it."`,
    tips: [
      "Follow up within 48 hours while the event is still fresh",
      "Reference a specific conversation detail to jog their memory",
      "Connect on LinkedIn immediately after the event with a personalized note",
      "Bring up something from their talk/panel if they were a speaker",
      "Suggest meeting at a location convenient for them"
    ]
  }
};

const INTERVIEW_PERSONA_GUIDES = {
  hr_manager: {
    name: "HR Manager",
    difficulty: "Medium",
    whatTheyCareAbout: [
      "Culture fit and team dynamics",
      "Process compliance - did you apply through proper channels?",
      "Communication skills and professionalism",
      "Your long-term career goals and stability"
    ],
    howTheyRespond: [
      "Professional and structured - follows a process",
      "May redirect you to the careers portal",
      "Screening questions about experience level and availability",
      "Polite but firm boundaries about next steps"
    ],
    howToWinThem: [
      "Be professional and respect their process",
      "Show you've researched the company culture",
      "Have a clear, concise summary of your background ready",
      "Ask about the team and work environment, not just the role"
    ],
    commonObjections: [
      "You need to apply through our portal first",
      "We're not hiring for that role right now",
      "Can you send me your resume?",
      "The hiring manager makes those decisions"
    ]
  },
  hiring_manager: {
    name: "Hiring Manager",
    difficulty: "Hard",
    whatTheyCareAbout: [
      "Can you actually do the job? Specific skills and experience",
      "What have you built, shipped, or accomplished?",
      "Will you fit with the existing team?",
      "How quickly can you ramp up and contribute?"
    ],
    howTheyRespond: [
      "Direct and technical - asks about specific projects",
      "Time-constrained and slightly skeptical of cold calls",
      "Tests your knowledge with probing questions",
      "Warms up quickly if you demonstrate real expertise"
    ],
    howToWinThem: [
      "Lead with specific, quantifiable accomplishments",
      "Show you understand THEIR specific challenges",
      "Be ready to discuss technical details of your work",
      "Demonstrate knowledge of their product/technology"
    ],
    commonObjections: [
      "I don't have time for this right now",
      "We use a formal interview process",
      "What specifically can you bring to my team?",
      "How did you get this number?"
    ]
  },
  cxo_vp: {
    name: "CXO / VP",
    difficulty: "Hard",
    whatTheyCareAbout: [
      "Strategic thinking and leadership potential",
      "Impact and results, not just tasks completed",
      "Confidence and executive presence",
      "Unique perspective or value-add"
    ],
    howTheyRespond: [
      "Very time-guarded - you have 30 seconds max",
      "Tests your confidence immediately",
      "May delegate to someone on their team",
      "Impressed by boldness and clarity"
    ],
    howToWinThem: [
      "Be concise - every word counts at this level",
      "Lead with impact and strategic value",
      "Show confidence without arrogance",
      "Have a unique angle or insight about their business"
    ],
    commonObjections: [
      "Talk to my team / HR handles this",
      "You have 30 seconds, go",
      "What makes you different from 100 other candidates?",
      "I don't handle hiring at this level"
    ]
  },
  recruiter: {
    name: "Recruiter (Staffing Agency)",
    difficulty: "Easy",
    whatTheyCareAbout: [
      "Are you a placeable candidate? Skills + availability",
      "Salary expectations and flexibility",
      "How quickly can you start?",
      "Do you fit any of their current open roles?"
    ],
    howTheyRespond: [
      "Warm and welcoming - they want to place you",
      "Ask structured screening questions",
      "Quick to assess fit for their open roles",
      "Transactional but helpful"
    ],
    howToWinThem: [
      "Be clear about what you're looking for (role, salary, location)",
      "Highlight in-demand skills and certifications",
      "Be flexible on start dates and work arrangements",
      "Send a clean, updated resume immediately after the call"
    ],
    commonObjections: [
      "I don't have anything matching your profile right now",
      "What's your salary expectation?",
      "Can you send me your updated resume?",
      "Are you open to contract or temp-to-hire?"
    ]
  },
  team_lead: {
    name: "Team Lead",
    difficulty: "Medium",
    whatTheyCareAbout: [
      "Will you fit into the team dynamic?",
      "Practical skills and hands-on experience",
      "Collaboration style and work ethic",
      "Technical depth in relevant areas"
    ],
    howTheyRespond: [
      "Casual and peer-like conversation style",
      "Asks about past projects and collaboration",
      "Evaluates your practical problem-solving approach",
      "More open than senior leadership"
    ],
    howToWinThem: [
      "Talk about specific projects and your role in them",
      "Show you're a team player with real examples",
      "Ask about their team's challenges and workflow",
      "Be authentic - they value genuineness over polish"
    ],
    commonObjections: [
      "I'd need to check with my manager",
      "We're pretty full right now",
      "What tools/technologies do you use?",
      "How do you handle tight deadlines?"
    ]
  },
  founder_startup: {
    name: "Startup Founder",
    difficulty: "Medium",
    whatTheyCareAbout: [
      "Hustle, adaptability, and passion",
      "Can you wear multiple hats?",
      "Do you believe in the mission?",
      "Culture add over culture fit"
    ],
    howTheyRespond: [
      "Energetic and fast-paced",
      "May get excited about your background and go on tangents",
      "Values passion and conviction over credentials",
      "Quick to make decisions - could fast-track you"
    ],
    howToWinThem: [
      "Show genuine enthusiasm for their product/mission",
      "Highlight adaptability and side projects",
      "Demonstrate you can operate in ambiguity",
      "Ask about their vision and biggest challenges"
    ],
    commonObjections: [
      "We're early stage - can you handle the uncertainty?",
      "What's your superpower?",
      "Why us over a big company with more stability?",
      "Can you work with limited resources?"
    ]
  }
};

const INTERVIEW_BEST_PRACTICES = [
  {
    title: "Before the Call",
    icon: "clipboard",
    tips: [
      "Research the company thoroughly: products, culture, recent news, LinkedIn of the person you're calling",
      "Prepare your 30-second elevator pitch and practice it out loud 5 times",
      "Know the role you're targeting and how your skills map to it",
      "Have your resume open in front of you for quick reference",
      "Prepare 3 key accomplishments with specific numbers/impact"
    ]
  },
  {
    title: "Your Elevator Pitch",
    icon: "mic",
    tips: [
      "Structure: Who you are → What you do → Your key achievement → Why this company",
      "Keep it under 30 seconds - time yourself",
      "Include one specific, impressive number (revenue, users, %, team size)",
      "End with a clear connection to their company/team",
      "Practice until it sounds natural, not rehearsed"
    ]
  },
  {
    title: "Handling 'We're Not Hiring'",
    icon: "shield",
    tips: [
      "Don't give up immediately - ask 'Is there anyone on your team I should connect with?'",
      "Pivot to informational: 'I understand. Would you have 5 minutes for career advice?'",
      "Ask about future plans: 'Are there any roles you anticipate opening in the next quarter?'",
      "Leave a lasting impression: 'Can I send you my portfolio for future reference?'",
      "Stay positive and gracious - they may reach out later"
    ]
  },
  {
    title: "Handling 'Apply Online'",
    icon: "redirect",
    tips: [
      "Don't resist - say 'Absolutely, I will. I actually already applied for [role]'",
      "Then add value: 'I wanted to connect personally because [specific reason]'",
      "Ask: 'Is there anything I should highlight in my application given your team's priorities?'",
      "Try to get a name: 'Who reviews applications for this role?'",
      "Follow up: 'Would it be okay if I followed up with you after applying?'"
    ]
  },
  {
    title: "Building Instant Rapport",
    icon: "handshake",
    tips: [
      "Be genuinely curious about them and their work",
      "Reference something specific from their LinkedIn profile or a talk they gave",
      "Match their energy and communication style",
      "Show vulnerability: 'I'll be honest, cold calling is outside my comfort zone, but I'm that excited about your company'",
      "Use their name naturally throughout the conversation"
    ]
  },
  {
    title: "Closing & Next Steps",
    icon: "trophy",
    tips: [
      "Always ask for a specific next step (meeting, resume review, referral)",
      "Offer flexibility: 'I'm happy to work around your schedule'",
      "Send a thank-you email within 2 hours of the call",
      "Connect on LinkedIn with a personalized note referencing the call",
      "Follow up once a week for 2-3 weeks if no response"
    ]
  }
];

export default function ColdCallLabDashboard() {
  const navigate = useNavigate();
  const { user, token, logout, isAuthenticated, loading: authLoading, updateUser } = useColdCallAuth();
  
  const [dashboardData, setDashboardData] = useState(null);
  const [categories, setCategories] = useState([]);
  const [personas, setPersonas] = useState([]);
  const [scenarios, setScenarios] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Session state
  const [showSessionSetup, setShowSessionSetup] = useState(false);
  const [practiceMode, setPracticeMode] = useState('sales'); // 'sales' or 'interview'
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedPersona, setSelectedPersona] = useState(null);
  const [selectedScenario, setSelectedScenario] = useState(null);
  const [activeSession, setActiveSession] = useState(null);
  const [sessionStartTime, setSessionStartTime] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  
  // Voice conversation state
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [conversation, setConversation] = useState([]);
  const [currentTranscript, setCurrentTranscript] = useState('');
  const [aiSpeaking, setAiSpeaking] = useState(false);
  
  // Refs to avoid stale closures in speech recognition callbacks
  const activeSessionRef = useRef(null);
  const aiSpeakingRef = useRef(false);
  const isProcessingRef = useRef(false);
  const conversationModeRef = useRef('continuous');
  const handleUserSpeechRef = useRef(null);
  
  // Enhanced UX state
  const [conversationMode, setConversationMode] = useState('continuous'); // 'continuous' or 'push-to-talk'
  const [silenceTimer, setSilenceTimer] = useState(null);
  const [audioLevel, setAudioLevel] = useState(0);
  const [showModeSelector, setShowModeSelector] = useState(false);
  
  // Keep refs in sync with state for speech recognition callbacks
  useEffect(() => { activeSessionRef.current = activeSession; }, [activeSession]);
  useEffect(() => { aiSpeakingRef.current = aiSpeaking; }, [aiSpeaking]);
  useEffect(() => { isProcessingRef.current = isProcessing; }, [isProcessing]);
  useEffect(() => { conversationModeRef.current = conversationMode; }, [conversationMode]);
  
  // Results state
  const [showResults, setShowResults] = useState(false);
  const [sessionResults, setSessionResults] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  
  // History state
  const [showHistory, setShowHistory] = useState(false);
  const [callHistory, setCallHistory] = useState([]);
  
  // Preparation Center state
  const [showPrepCenter, setShowPrepCenter] = useState(false);
  const [prepTab, setPrepTab] = useState('scripts'); // scripts, personas, tips
  const [selectedScript, setSelectedScript] = useState(null);
  const [selectedPersonaGuide, setSelectedPersonaGuide] = useState(null);
  const [expandedTips, setExpandedTips] = useState({});
  
  // Interview Prep Center state
  const [showInterviewPrepCenter, setShowInterviewPrepCenter] = useState(false);
  const [interviewPrepTab, setInterviewPrepTab] = useState('scripts');
  const [selectedInterviewScript, setSelectedInterviewScript] = useState(null);
  const [selectedInterviewPersonaGuide, setSelectedInterviewPersonaGuide] = useState(null);
  const [expandedInterviewTips, setExpandedInterviewTips] = useState({});
  
  // Helper for best practice icons
  const getPracticeIcon = (iconKey) => {
    const icons = {
      clipboard: <FileText className="h-5 w-5" />,
      target: <Target className="h-5 w-5" />,
      handshake: <Users className="h-5 w-5" />,
      shield: <Shield className="h-5 w-5" />,
      trophy: <Star className="h-5 w-5" />,
      notepad: <BookOpen className="h-5 w-5" />,
      mic: <Mic className="h-5 w-5" />,
      redirect: <ArrowRight className="h-5 w-5" />,
    };
    return icons[iconKey] || <Lightbulb className="h-5 w-5" />;
  };
  
  // Refs
  const timerRef = useRef(null);
  const recognitionRef = useRef(null);
  const audioRef = useRef(null);
  const conversationEndRef = useRef(null);
  const silenceTimerRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const micStreamRef = useRef(null);
  const animationFrameRef = useRef(null);

  // Auth check
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/cold-call-lab');
    }
  }, [authLoading, isAuthenticated, navigate]);

  // Fetch dashboard data
  useEffect(() => {
    if (token) {
      fetchDashboardData();
      fetchOptions();
    }
  }, [token]);

  // Timer for active session
  useEffect(() => {
    if (activeSession && sessionStartTime) {
      timerRef.current = setInterval(() => {
        setElapsedTime(Math.floor((Date.now() - sessionStartTime) / 1000));
      }, 1000);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [activeSession, sessionStartTime]);

  // Auto-scroll conversation
  useEffect(() => {
    if (conversationEndRef.current) {
      conversationEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [conversation]);

  // Initialize speech recognition ONCE - use refs for all state checks
  useEffect(() => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = true;
      recognitionRef.current.interimResults = true;
      recognitionRef.current.lang = 'en-US';
      recognitionRef.current.maxAlternatives = 1;

      let finalTranscriptBuffer = '';
      
      recognitionRef.current.onresult = (event) => {
        let interimTranscript = '';

        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalTranscriptBuffer += transcript + ' ';
          } else {
            interimTranscript = transcript;
          }
        }

        // Show what's being heard
        setCurrentTranscript(finalTranscriptBuffer + interimTranscript);

        // Clear any existing silence timer
        if (silenceTimerRef.current) {
          clearTimeout(silenceTimerRef.current);
          silenceTimerRef.current = null;
        }

        // If we have accumulated final transcript, set up auto-send
        if (finalTranscriptBuffer.trim()) {
          if (conversationModeRef.current === 'push-to-talk') {
            // User will manually send - don't auto-send
          } else {
            // In continuous mode, wait for silence before sending
            silenceTimerRef.current = setTimeout(() => {
              if (finalTranscriptBuffer.trim() && !isProcessingRef.current && !aiSpeakingRef.current) {
                const textToSend = finalTranscriptBuffer.trim();
                finalTranscriptBuffer = ''; // Reset buffer
                if (handleUserSpeechRef.current) {
                  handleUserSpeechRef.current(textToSend);
                }
              }
            }, 1200);
          }
        }
      };

      recognitionRef.current.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        if (event.error !== 'no-speech' && event.error !== 'aborted') {
          if (event.error === 'network') {
            toast.error('Network error. Please check your connection.');
          }
        }
      };

      recognitionRef.current.onend = () => {
        // Always restart if we should be listening (use refs for fresh values)
        if (activeSessionRef.current && !aiSpeakingRef.current && !isProcessingRef.current) {
          try {
            setTimeout(() => {
              if (recognitionRef.current && activeSessionRef.current && !aiSpeakingRef.current && !isProcessingRef.current) {
                recognitionRef.current.start();
                setIsListening(true);
              }
            }, 100);
          } catch (e) {
            console.log('Recognition restart handled');
          }
        }
      };
    }

    return () => {
      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
        } catch (e) {}
      }
      if (silenceTimerRef.current) {
        clearTimeout(silenceTimerRef.current);
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (micStreamRef.current) {
        micStreamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, []); // Empty deps - create once, use refs for fresh state

  const fetchDashboardData = async () => {
    try {
      const response = await axios.get(`${API}/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboardData(response.data);
      updateUser(response.data.user);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
      if (error.response?.status === 401) {
        logout();
        navigate('/cold-call-lab');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchOptions = async (mode) => {
    try {
      const m = mode || practiceMode;
      const [catRes, persRes, scenRes] = await Promise.all([
        axios.get(`${API}/categories?mode=${m}`),
        axios.get(`${API}/personas?mode=${m}`),
        axios.get(`${API}/scenarios?mode=${m}`)
      ]);
      setCategories(catRes.data.categories);
      setPersonas(persRes.data.personas);
      setScenarios(scenRes.data.scenarios);
      // Reset selections when mode changes
      setSelectedCategory(null);
      setSelectedPersona(null);
      setSelectedScenario(null);
    } catch (error) {
      console.error('Error fetching options:', error);
    }
  };

  const fetchHistory = async () => {
    try {
      const response = await axios.get(`${API}/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCallHistory(response.data.history);
    } catch (error) {
      console.error('Error fetching history:', error);
    }
  };

  const startSession = async () => {
    if (!selectedCategory || !selectedPersona || !selectedScenario) {
      toast.error('Please select category, persona, and scenario');
      return;
    }

    try {
      const response = await axios.post(`${API}/session/start`, {
        category: selectedCategory,
        persona: selectedPersona,
        scenario: selectedScenario,
        mode: practiceMode
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setActiveSession(response.data);
      setSessionStartTime(Date.now());
      setElapsedTime(0);
      setConversation([]);
      setCurrentTranscript('');
      setShowSessionSetup(false);
      
      toast.success(practiceMode === 'interview' 
        ? 'Call connected! Start your interview outreach.' 
        : 'Call connected! Start speaking to the prospect.');
      
      // Auto-start listening after a brief delay
      setTimeout(() => {
        startListening();
      }, 500);
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start session');
    }
  };

  const startListening = async () => {
    if (recognitionRef.current && !isListening) {
      try {
        // Start audio level monitoring
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        micStreamRef.current = stream;
        
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
        analyserRef.current = audioContextRef.current.createAnalyser();
        const source = audioContextRef.current.createMediaStreamSource(stream);
        source.connect(analyserRef.current);
        analyserRef.current.fftSize = 256;
        
        const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
        
        const updateAudioLevel = () => {
          if (analyserRef.current && isListening) {
            analyserRef.current.getByteFrequencyData(dataArray);
            const average = dataArray.reduce((a, b) => a + b) / dataArray.length;
            setAudioLevel(Math.min(100, average * 1.5));
            animationFrameRef.current = requestAnimationFrame(updateAudioLevel);
          }
        };
        
        updateAudioLevel();
        recognitionRef.current.start();
        setIsListening(true);
      } catch (e) {
        console.error('Error starting recognition:', e);
        toast.error('Please allow microphone access to start the call');
      }
    }
  };

  const stopListening = () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      setIsListening(false);
    }
    // Clean up audio monitoring
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
    if (micStreamRef.current) {
      micStreamRef.current.getTracks().forEach(track => track.stop());
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
    }
    setAudioLevel(0);
  };

  const handleUserSpeech = useCallback(async (text) => {
    if (!activeSession || isProcessing || !text.trim()) return;

    setIsProcessing(true);
    setCurrentTranscript('');
    
    // Stop listening while processing and AI responds
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch (e) {}
    }
    setIsListening(false);

    // Add user message to conversation
    const userMessage = { role: 'user', content: text.trim() };
    setConversation(prev => [...prev, userMessage]);

    try {
      // Send to AI and get response with audio
      const response = await axios.post(
        `${API}/session/${activeSession.session_id}/converse`,
        { text: text.trim(), session_id: activeSession.session_id },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const { ai_response, audio_base64 } = response.data;

      // Add AI response to conversation
      const aiMessage = { role: 'ai', content: ai_response };
      setConversation(prev => [...prev, aiMessage]);

      // Play AI audio response
      if (audio_base64) {
        setAiSpeaking(true);
        const audio = new Audio(`data:audio/mp3;base64,${audio_base64}`);
        audioRef.current = audio;
        
        audio.onended = () => {
          setAiSpeaking(false);
          setIsProcessing(false);
          // Automatically resume listening after AI finishes speaking
          if (activeSessionRef.current) {
            resumeListeningAfterAI();
          }
        };
        
        audio.onerror = () => {
          setAiSpeaking(false);
          setIsProcessing(false);
          if (activeSessionRef.current) {
            resumeListeningAfterAI();
          }
        };
        
        await audio.play();
      } else {
        // No audio response, resume listening immediately
        setIsProcessing(false);
        if (activeSessionRef.current) {
          resumeListeningAfterAI();
        }
      }

    } catch (error) {
      console.error('Error in conversation:', error);
      toast.error('Failed to get AI response. Try speaking again.');
      setIsProcessing(false);
      // Resume listening on error
      if (activeSessionRef.current) {
        resumeListeningAfterAI();
      }
    }
  }, [activeSession, isProcessing, token]);
  
  // Keep handleUserSpeech ref in sync for speech recognition callbacks
  useEffect(() => { handleUserSpeechRef.current = handleUserSpeech; }, [handleUserSpeech]);

  // Dedicated function to resume listening after AI finishes
  const resumeListeningAfterAI = useCallback(async () => {
    // Small delay to ensure clean transition
    await new Promise(resolve => setTimeout(resolve, 300));
    
    if (!activeSessionRef.current || aiSpeakingRef.current || isProcessingRef.current) return;
    
    try {
      // Request microphone access if needed
      if (!micStreamRef.current || micStreamRef.current.getTracks().every(t => t.readyState === 'ended')) {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        micStreamRef.current = stream;
        
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
        analyserRef.current = audioContextRef.current.createAnalyser();
        const source = audioContextRef.current.createMediaStreamSource(stream);
        source.connect(analyserRef.current);
        analyserRef.current.fftSize = 256;
        
        const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
        
        const updateAudioLevel = () => {
          if (analyserRef.current && isListening) {
            analyserRef.current.getByteFrequencyData(dataArray);
            const average = dataArray.reduce((a, b) => a + b) / dataArray.length;
            setAudioLevel(Math.min(100, average * 1.5));
            animationFrameRef.current = requestAnimationFrame(updateAudioLevel);
          }
        };
        
        updateAudioLevel();
      }
      
      // Start speech recognition
      if (recognitionRef.current) {
        recognitionRef.current.start();
        setIsListening(true);
      }
    } catch (e) {
      console.error('Error resuming listening:', e);
      // Try simple restart
      try {
        if (recognitionRef.current) {
          recognitionRef.current.start();
          setIsListening(true);
        }
      } catch (e2) {
        console.log('Recognition will restart on next attempt');
      }
    }
  }, []); // Uses refs, no state deps needed

  const toggleMic = () => {
    if (isListening) {
      stopListening();
    } else {
      startListening();
    }
  };

  // Manual send function for push-to-talk mode
  const handleManualSend = () => {
    if (currentTranscript.trim() && !isProcessing && !aiSpeaking) {
      if (silenceTimerRef.current) {
        clearTimeout(silenceTimerRef.current);
        silenceTimerRef.current = null;
      }
      handleUserSpeech(currentTranscript);
    }
  };

  const endSession = async () => {
    // Stop listening and audio
    stopListening();
    if (audioRef.current) {
      audioRef.current.pause();
    }
    
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    if (conversation.length < 2) {
      toast.error('Please have at least one exchange before ending the call');
      return;
    }

    setIsAnalyzing(true);

    try {
      // Get AI analysis of the call
      const response = await axios.post(
        `${API}/session/${activeSession.session_id}/analyze`,
        {
          session_id: activeSession.session_id,
          transcript: conversation,
          duration_seconds: elapsedTime
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setSessionResults(response.data);
      setShowResults(true);
      setActiveSession(null);
      setSessionStartTime(null);
      setConversation([]);
      fetchDashboardData();
      
    } catch (error) {
      console.error('Error analyzing call:', error);
      toast.error(error.response?.data?.detail || 'Failed to analyze call');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleLogout = () => {
    logout();
    navigate('/cold-call-lab');
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-cyan-500 animate-spin" />
      </div>
    );
  }

  const stats = dashboardData?.stats || {};

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <header className="border-b border-white/10 backdrop-blur-sm bg-white/5 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-gradient-to-r from-cyan-500 to-blue-500 p-2 rounded-lg">
              <Phone className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">HFS Cold Call Lab</h1>
              <p className="text-xs text-slate-400">Welcome, {user?.name}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              onClick={() => { fetchHistory(); setShowHistory(true); }}
              className="text-slate-400 hover:text-white"
              data-testid="history-btn"
            >
              <History className="h-5 w-5" />
            </Button>
            <Button
              variant="ghost"
              onClick={handleLogout}
              className="text-slate-400 hover:text-white"
              data-testid="logout-btn"
            >
              <LogOut className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Welcome Section */}
        {!activeSession && (
          <div className="mb-8">
            <h2 className="text-3xl font-bold text-white mb-2">
              Welcome back, <span className="text-cyan-500">{user?.name?.split(' ')[0]}</span>
            </h2>
            <p className="text-slate-400">Ready to sharpen your cold calling skills?</p>
          </div>
        )}

        {/* Pending Approval Message */}
        {user?.status === 'pending' && (
          <Card className="bg-amber-500/10 border-amber-500/30 mb-8">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="bg-amber-500/20 p-3 rounded-full">
                  <Clock className="h-8 w-8 text-amber-500" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-amber-400 mb-1">Account Pending Approval</h3>
                  <p className="text-slate-300">
                    Your registration is being reviewed by the admin team. You'll be able to start practicing 
                    once your account is approved. Check back soon!
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Rejected Message */}
        {user?.status === 'rejected' && (
          <Card className="bg-red-500/10 border-red-500/30 mb-8">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="bg-red-500/20 p-3 rounded-full">
                  <AlertCircle className="h-8 w-8 text-red-500" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-red-400 mb-1">Account Not Approved</h3>
                  <p className="text-slate-300">
                    Unfortunately, your account was not approved. Please contact the admin team for more information.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Active Call Session UI */}
        {activeSession && (
          <div className="space-y-6">
            {/* Call Header with Turn Indicator */}
            <Card className="bg-gradient-to-r from-green-900/50 to-emerald-900/50 border-green-500/30">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`p-3 rounded-full transition-all duration-300 ${
                      aiSpeaking ? 'bg-green-500 animate-pulse scale-110' : 
                      isListening ? 'bg-cyan-500 animate-pulse' : 'bg-green-500/20'
                    }`}>
                      <PhoneCall className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white">
                        {activeSession.mode === 'interview' ? 'Interview Call with' : 'Call with'} {activeSession.persona.name}
                      </h3>
                      <p className="text-slate-300 text-sm">
                        {activeSession.mode === 'interview' && <span className="text-emerald-400 mr-1">Interview Outreach</span>}
                        {activeSession.category.name} • {activeSession.scenario.name}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-3xl font-mono font-bold text-green-400" data-testid="call-timer">
                      {formatTime(elapsedTime)}
                    </div>
                    <p className="text-slate-400 text-sm">Duration</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Turn Indicator Banner with Audio Visualization */}
            <div className={`rounded-xl p-4 transition-all duration-500 ${
              aiSpeaking 
                ? 'bg-gradient-to-r from-green-600 to-emerald-600 shadow-lg shadow-green-500/30' 
                : isListening 
                  ? 'bg-gradient-to-r from-cyan-600 to-blue-600 shadow-lg shadow-cyan-500/30'
                  : isProcessing
                    ? 'bg-gradient-to-r from-amber-600 to-orange-600 shadow-lg shadow-amber-500/30'
                    : 'bg-slate-700/50'
            }`}>
              <div className="flex flex-col items-center gap-3">
                {/* Main Status */}
                <div className="flex items-center justify-center gap-3">
                  {aiSpeaking ? (
                    <>
                      <div className="flex items-center gap-1">
                        <span className="w-1 h-4 bg-white rounded animate-pulse" style={{animationDelay: '0ms'}}></span>
                        <span className="w-1 h-6 bg-white rounded animate-pulse" style={{animationDelay: '150ms'}}></span>
                        <span className="w-1 h-8 bg-white rounded animate-pulse" style={{animationDelay: '300ms'}}></span>
                        <span className="w-1 h-6 bg-white rounded animate-pulse" style={{animationDelay: '450ms'}}></span>
                        <span className="w-1 h-4 bg-white rounded animate-pulse" style={{animationDelay: '600ms'}}></span>
                      </div>
                      <span className="text-white font-bold text-lg">{activeSession.persona.name} is speaking...</span>
                      <Volume2 className="h-6 w-6 text-white animate-pulse" />
                    </>
                  ) : isListening ? (
                    <>
                      <Mic className="h-6 w-6 text-white" />
                      <span className="text-white font-bold text-lg">YOUR TURN - Speak now!</span>
                    </>
                  ) : isProcessing ? (
                    <>
                      <Loader2 className="h-6 w-6 text-white animate-spin" />
                      <span className="text-white font-bold text-lg">Processing your response...</span>
                    </>
                  ) : (
                    <>
                      <MicOff className="h-6 w-6 text-slate-400" />
                      <span className="text-slate-400 font-medium">Microphone paused - Click to resume</span>
                    </>
                  )}
                </div>
                
                {/* Audio Level Visualization - Only when listening */}
                {isListening && (
                  <div className="w-full max-w-md">
                    <div className="flex items-center justify-center gap-1 h-8">
                      {/* Dynamic audio bars based on audio level */}
                      {[...Array(20)].map((_, i) => {
                        const barHeight = Math.min(100, Math.max(20, audioLevel + Math.sin(i * 0.5) * 20));
                        const isActive = (i / 20) * 100 < audioLevel;
                        return (
                          <div
                            key={i}
                            className={`w-1.5 rounded-full transition-all duration-75 ${
                              isActive ? 'bg-white' : 'bg-white/30'
                            }`}
                            style={{ 
                              height: `${isActive ? barHeight * 0.3 : 8}px`,
                              opacity: isActive ? 1 : 0.3
                            }}
                          />
                        );
                      })}
                    </div>
                    <p className="text-white/70 text-xs text-center mt-1">
                      {audioLevel > 50 ? 'Good audio level' : audioLevel > 20 ? 'Speak a bit louder' : 'Listening...'}
                    </p>
                  </div>
                )}

                {/* Silence indicator in continuous mode */}
                {isListening && conversationMode === 'continuous' && currentTranscript && (
                  <p className="text-white/60 text-xs">
                    Will send after brief pause...
                  </p>
                )}
                
                {/* Seamless conversation hint */}
                {isListening && !currentTranscript && conversation.length > 0 && (
                  <p className="text-cyan-400/70 text-xs animate-pulse">
                    Your turn to speak...
                  </p>
                )}
              </div>
            </div>

            {/* Conversation Display */}
            <Card className="bg-white/5 border-white/10">
              <CardHeader className="border-b border-white/10 py-3">
                <CardTitle className="text-white flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <MessageSquare className="h-5 w-5 text-cyan-500" />
                    Conversation
                  </div>
                  <span className="text-sm text-slate-400 font-normal">
                    {conversation.length} exchanges
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4">
                <div className="h-80 overflow-y-auto space-y-4 mb-4 scroll-smooth">
                  {conversation.length === 0 && (
                    <div className="text-center text-slate-400 py-12">
                      <div className="bg-cyan-500/10 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Mic className="h-10 w-10 text-cyan-500 animate-pulse" />
                      </div>
                      <p className="text-lg font-medium text-white mb-2">Just start speaking!</p>
                      <p className="text-sm">The conversation will flow naturally - no buttons needed.</p>
                      <p className="text-xs mt-2 text-cyan-400">Say your opening line to {activeSession.persona.name}</p>
                      <p className="text-xs mt-1 text-slate-500">Example: "Hi, this is John from TechSolutions..."</p>
                    </div>
                  )}
                  
                  {conversation.map((msg, idx) => (
                    <div
                      key={idx}
                      className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[80%] rounded-2xl p-4 ${
                          msg.role === 'user'
                            ? 'bg-gradient-to-br from-cyan-500/30 to-blue-500/30 text-cyan-100 border border-cyan-500/40'
                            : 'bg-gradient-to-br from-slate-700/70 to-slate-600/70 text-slate-100 border border-slate-500/40'
                        }`}
                      >
                        <div className="flex items-center gap-2 mb-2">
                          {msg.role === 'user' ? (
                            <div className="bg-cyan-500 w-6 h-6 rounded-full flex items-center justify-center">
                              <Users className="h-3 w-3 text-white" />
                            </div>
                          ) : (
                            <div className="bg-slate-500 w-6 h-6 rounded-full flex items-center justify-center">
                              <Phone className="h-3 w-3 text-white" />
                            </div>
                          )}
                          <span className="text-xs font-medium text-slate-300">
                            {msg.role === 'user' ? 'You' : activeSession.persona.name}
                          </span>
                        </div>
                        <p className="text-sm leading-relaxed">{msg.content}</p>
                      </div>
                    </div>
                  ))}
                  
                  {/* Current transcript (what's being spoken) */}
                  {currentTranscript && (
                    <div className="flex justify-end">
                      <div className="max-w-[80%] rounded-2xl p-4 bg-cyan-500/10 text-cyan-100/70 border-2 border-dashed border-cyan-500/40">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="bg-cyan-500/50 w-6 h-6 rounded-full flex items-center justify-center">
                            <Mic className="h-3 w-3 text-white animate-pulse" />
                          </div>
                          <span className="text-xs font-medium text-cyan-400">You (speaking...)</span>
                        </div>
                        <p className="text-sm">{currentTranscript}</p>
                      </div>
                    </div>
                  )}
                  
                  {/* Processing indicator */}
                  {isProcessing && (
                    <div className="flex justify-start">
                      <div className="bg-gradient-to-br from-slate-700/70 to-slate-600/70 rounded-2xl p-4 border border-slate-500/40">
                        <div className="flex items-center gap-3">
                          <div className="bg-amber-500 w-6 h-6 rounded-full flex items-center justify-center">
                            <Loader2 className="h-3 w-3 text-white animate-spin" />
                          </div>
                          <span className="text-slate-300 text-sm">{activeSession.persona.name} is thinking...</span>
                          <div className="flex gap-1">
                            <span className="w-2 h-2 bg-amber-500 rounded-full animate-bounce" style={{animationDelay: '0ms'}}></span>
                            <span className="w-2 h-2 bg-amber-500 rounded-full animate-bounce" style={{animationDelay: '150ms'}}></span>
                            <span className="w-2 h-2 bg-amber-500 rounded-full animate-bounce" style={{animationDelay: '300ms'}}></span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <div ref={conversationEndRef} />
                </div>

                {/* Mic Controls - Enhanced */}
                <div className="space-y-3">
                  {/* Mode selector */}
                  <div className="flex items-center justify-between bg-slate-900/50 rounded-lg p-2">
                    <span className="text-xs text-slate-400 ml-2">Conversation Mode:</span>
                    <div className="flex gap-1">
                      <button
                        onClick={() => setConversationMode('continuous')}
                        className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                          conversationMode === 'continuous' 
                            ? 'bg-cyan-500 text-white' 
                            : 'bg-slate-700 text-slate-400 hover:bg-slate-600'
                        }`}
                        data-testid="mode-continuous-btn"
                      >
                        Auto-Send
                      </button>
                      <button
                        onClick={() => setConversationMode('push-to-talk')}
                        className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                          conversationMode === 'push-to-talk' 
                            ? 'bg-cyan-500 text-white' 
                            : 'bg-slate-700 text-slate-400 hover:bg-slate-600'
                        }`}
                        data-testid="mode-push-to-talk-btn"
                      >
                        Manual Send
                      </button>
                    </div>
                  </div>
                  
                  {/* Main controls */}
                  <div className="flex items-center justify-between bg-slate-800/70 rounded-xl p-4 border border-slate-700/50">
                    <div className="flex items-center gap-3">
                      {aiSpeaking ? (
                        <div className="flex items-center gap-2 text-green-400">
                          <Volume2 className="h-5 w-5 animate-pulse" />
                          <span className="font-medium">Prospect speaking - listen carefully</span>
                        </div>
                      ) : isListening ? (
                        <div className="flex items-center gap-2 text-cyan-400">
                          <Mic className="h-5 w-5 animate-pulse" />
                          <span className="font-medium">
                            {conversationMode === 'continuous' 
                              ? 'Listening... (auto-send after pause)' 
                              : 'Listening... click Send when ready'}
                          </span>
                        </div>
                      ) : isProcessing ? (
                        <div className="flex items-center gap-2 text-amber-400">
                          <Loader2 className="h-5 w-5 animate-spin" />
                          <span className="font-medium">Processing response...</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 text-slate-400">
                          <MicOff className="h-5 w-5" />
                          <span>Microphone off</span>
                        </div>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-2">
                      {/* Manual Send Button (only in push-to-talk mode when there's text) */}
                      {conversationMode === 'push-to-talk' && currentTranscript && !isProcessing && !aiSpeaking && (
                        <Button
                          onClick={handleManualSend}
                          size="lg"
                          className="bg-green-500 hover:bg-green-600 shadow-lg shadow-green-500/30"
                          data-testid="manual-send-btn"
                        >
                          <ChevronRight className="h-5 w-5 mr-1" />
                          Send
                        </Button>
                      )}
                      
                      <Button
                        onClick={toggleMic}
                        disabled={aiSpeaking || isProcessing}
                        size="lg"
                        className={`transition-all duration-300 ${
                          isListening 
                            ? 'bg-red-500 hover:bg-red-600 shadow-lg shadow-red-500/30' 
                            : 'bg-cyan-500 hover:bg-cyan-600 shadow-lg shadow-cyan-500/30'
                        }`}
                        data-testid="mic-toggle-btn"
                      >
                        {isListening ? (
                          <>
                            <MicOff className="h-5 w-5 mr-2" />
                            Pause Mic
                          </>
                        ) : (
                          <>
                            <Mic className="h-5 w-5 mr-2" />
                            Resume Mic
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Call Controls */}
            <div className="flex items-center justify-center gap-4">
              <Button
                onClick={endSession}
                disabled={isAnalyzing}
                size="lg"
                className="bg-red-500 hover:bg-red-600 text-white px-8 shadow-lg shadow-red-500/30"
                data-testid="end-call-btn"
              >
                {isAnalyzing ? (
                  <>
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                    Analyzing Call...
                  </>
                ) : (
                  <>
                    <PhoneOff className="h-5 w-5 mr-2" />
                    End Call & Get Feedback
                  </>
                )}
              </Button>
            </div>

            {/* Persona Info Card */}
            <Card className="bg-white/5 border-white/10">
              <CardContent className="p-4">
                <p className="text-sm text-slate-400 mb-1">
                  <strong>Persona:</strong> {activeSession.persona.name} ({activeSession.persona.difficulty})
                </p>
                <p className="text-slate-300 text-sm">{activeSession.persona.traits}</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Start Practice Call Card - Only for approved users */}
        {!activeSession && user?.status === 'approved' && (
          <Card 
            className="bg-gradient-to-r from-cyan-900/30 to-blue-900/30 border-cyan-500/30 mb-4 hover:from-cyan-900/50 hover:to-blue-900/50 transition-all cursor-pointer" 
            onClick={() => setShowSessionSetup(true)}
            data-testid="start-practice-card"
          >
            <CardContent className="p-8">
              <div className="flex items-center gap-6">
                <div className="bg-gradient-to-r from-cyan-500 to-blue-500 p-5 rounded-2xl">
                  <PhoneCall className="h-10 w-10 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-white mb-2">Start Practice Call</h3>
                  <p className="text-slate-400">
                    Practice sales cold calls or interview outreach with AI-powered simulations. 
                    Select a mode, persona, speak naturally, and get instant feedback.
                  </p>
                  <div className="flex gap-3 mt-3">
                    <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-cyan-500/15 text-cyan-400 text-xs font-medium border border-cyan-500/20">
                      <PhoneCall className="h-3 w-3" /> Sales Practice
                    </span>
                    <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-emerald-500/15 text-emerald-400 text-xs font-medium border border-emerald-500/20">
                      <Briefcase className="h-3 w-3" /> Interview Outreach
                    </span>
                  </div>
                </div>
                <ChevronRight className="h-8 w-8 text-cyan-400" />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Preparation Center Card - Only for approved users */}
        {!activeSession && user?.status === 'approved' && (
          <div className="grid md:grid-cols-2 gap-4 mb-8">
            <Card 
              className="bg-gradient-to-r from-purple-900/30 to-indigo-900/30 border-purple-500/30 hover:from-purple-900/50 hover:to-indigo-900/50 transition-all cursor-pointer" 
              onClick={() => setShowPrepCenter(true)}
              data-testid="prep-center-card"
            >
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="bg-gradient-to-r from-purple-500 to-indigo-500 p-4 rounded-2xl flex-shrink-0">
                    <BookOpen className="h-8 w-8 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-bold text-white mb-1">Sales Prep Center</h3>
                    <p className="text-slate-400 text-sm">
                      Scripts, persona guides & objection playbooks for sales calls
                    </p>
                  </div>
                  <ChevronRight className="h-6 w-6 text-purple-400 flex-shrink-0" />
                </div>
              </CardContent>
            </Card>
            <Card 
              className="bg-gradient-to-r from-emerald-900/30 to-teal-900/30 border-emerald-500/30 hover:from-emerald-900/50 hover:to-teal-900/50 transition-all cursor-pointer" 
              onClick={() => setShowInterviewPrepCenter(true)}
              data-testid="interview-prep-center-card"
            >
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="bg-gradient-to-r from-emerald-500 to-teal-500 p-4 rounded-2xl flex-shrink-0">
                    <Briefcase className="h-8 w-8 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-bold text-white mb-1">Interview Prep Center</h3>
                    <p className="text-slate-400 text-sm">
                      Elevator pitches, HR objection playbooks & outreach scripts
                    </p>
                  </div>
                  <ChevronRight className="h-6 w-6 text-emerald-400 flex-shrink-0" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Stats Grid */}
        {!activeSession && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <Card className="bg-white/5 border-white/10">
                <CardContent className="p-4 text-center">
                  <Phone className="h-6 w-6 text-cyan-500 mx-auto mb-2" />
                  <div className="text-3xl font-bold text-white" data-testid="total-calls">{stats.total_calls || 0}</div>
                  <p className="text-slate-400 text-sm">Total Calls</p>
                </CardContent>
              </Card>
              <Card className="bg-white/5 border-white/10">
                <CardContent className="p-4 text-center">
                  <Target className="h-6 w-6 text-cyan-500 mx-auto mb-2" />
                  <div className="text-3xl font-bold text-white" data-testid="avg-score">{stats.avg_score || 0}</div>
                  <p className="text-slate-400 text-sm">Avg Score</p>
                </CardContent>
              </Card>
              <Card className="bg-white/5 border-white/10">
                <CardContent className="p-4 text-center">
                  <TrendingUp className="h-6 w-6 text-cyan-500 mx-auto mb-2" />
                  <div className="text-3xl font-bold text-white">{stats.categories_practiced || 0}/{stats.total_categories || 7}</div>
                  <p className="text-slate-400 text-sm">Categories</p>
                </CardContent>
              </Card>
              <Card className="bg-white/5 border-white/10">
                <CardContent className="p-4 text-center">
                  <Users className="h-6 w-6 text-cyan-500 mx-auto mb-2" />
                  <div className="text-3xl font-bold text-white">{stats.personas_practiced || 0}/{stats.total_personas || 6}</div>
                  <p className="text-slate-400 text-sm">Personas</p>
                </CardContent>
              </Card>
            </div>

            {/* Progress Section */}
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              {/* Your Progress */}
              <Card className="bg-white/5 border-white/10">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <TrendingUp className="h-5 w-5 text-cyan-500" />
                    Your Progress
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-slate-400">Categories Practiced</span>
                      <span className="text-white">{stats.categories_practiced || 0} / {stats.total_categories || 7}</span>
                    </div>
                    <Progress value={((stats.categories_practiced || 0) / (stats.total_categories || 7)) * 100} className="h-2" />
                  </div>
                  <div>
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-slate-400">Personas Practiced</span>
                      <span className="text-white">{stats.personas_practiced || 0} / {stats.total_personas || 6}</span>
                    </div>
                    <Progress value={((stats.personas_practiced || 0) / (stats.total_personas || 6)) * 100} className="h-2" />
                  </div>
                </CardContent>
              </Card>

              {/* Achievements */}
              <Card className="bg-white/5 border-white/10">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Trophy className="h-5 w-5 text-amber-500" />
                    Achievements
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {dashboardData?.achievements?.length > 0 ? (
                    <div className="space-y-3">
                      {dashboardData.achievements.map((achievement, idx) => (
                        <div key={idx} className="flex items-center gap-3 bg-white/5 rounded-lg p-3">
                          <Award className="h-5 w-5 text-amber-500" />
                          <span className="text-white">{achievement.name}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-slate-400 text-center py-4">
                      Complete calls to earn achievements!
                    </p>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Recent Calls */}
            <Card className="bg-white/5 border-white/10">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Clock className="h-5 w-5 text-cyan-500" />
                  Recent Calls
                </CardTitle>
              </CardHeader>
              <CardContent>
                {dashboardData?.recent_calls?.length > 0 ? (
                  <div className="space-y-3">
                    {dashboardData.recent_calls.map((call, idx) => (
                      <div key={idx} className="flex items-center justify-between bg-white/5 rounded-lg p-4">
                        <div>
                          <p className="text-white font-medium">{call.persona}</p>
                          <p className="text-slate-400 text-sm">{call.category} • {call.scenario}</p>
                        </div>
                        <div className="text-right">
                          <p className={`font-bold ${
                            call.score >= 80 ? 'text-green-400' :
                            call.score >= 60 ? 'text-amber-400' : 'text-red-400'
                          }`}>{call.score}</p>
                          <p className="text-slate-400 text-sm">{formatTime(call.duration)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-400 text-center py-8">
                    No calls yet. Start your first practice session!
                  </p>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </main>

      {/* Session Setup Modal */}
      {showSessionSetup && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-2xl bg-slate-900 border-white/10 relative max-h-[90vh] overflow-y-auto">
            <button
              onClick={() => setShowSessionSetup(false)}
              className="absolute top-4 right-4 text-slate-400 hover:text-white z-10"
            >
              <X className="h-5 w-5" />
            </button>
            <CardHeader>
              <CardTitle className="text-white">Setup Your Practice Call</CardTitle>
              <p className="text-slate-400 text-sm">
                {practiceMode === 'interview' 
                  ? 'Practice cold outreach calls to HRs & leaders for job opportunities'
                  : 'Select category, persona, and scenario to begin your AI-powered call'}
              </p>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Practice Mode Toggle */}
              <div>
                <label className="text-sm font-medium text-slate-300 mb-3 block">Practice Mode</label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => { setPracticeMode('sales'); fetchOptions('sales'); }}
                    className={`p-4 rounded-lg border text-left transition-all ${
                      practiceMode === 'sales'
                        ? 'bg-cyan-500/20 border-cyan-500 text-white ring-1 ring-cyan-500/50'
                        : 'bg-white/5 border-white/10 text-slate-300 hover:bg-white/10'
                    }`}
                    data-testid="mode-sales-btn"
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <PhoneCall className="h-4 w-4" />
                      <p className="font-semibold">Sales Practice</p>
                    </div>
                    <p className="text-xs text-slate-400">Cold call prospects to sell SaaS products</p>
                  </button>
                  <button
                    onClick={() => { setPracticeMode('interview'); fetchOptions('interview'); }}
                    className={`p-4 rounded-lg border text-left transition-all ${
                      practiceMode === 'interview'
                        ? 'bg-emerald-500/20 border-emerald-500 text-white ring-1 ring-emerald-500/50'
                        : 'bg-white/5 border-white/10 text-slate-300 hover:bg-white/10'
                    }`}
                    data-testid="mode-interview-btn"
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <Briefcase className="h-4 w-4" />
                      <p className="font-semibold">Interview Outreach</p>
                    </div>
                    <p className="text-xs text-slate-400">Cold call HRs & leaders to ask for interviews</p>
                  </button>
                </div>
              </div>

              {/* Category Selection */}
              <div>
                <label className="text-sm font-medium text-slate-300 mb-3 block">
                  1. {practiceMode === 'interview' ? 'Select Industry / Company Type' : 'Select SaaS Category'}
                </label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {categories.map((cat) => (
                    <button
                      key={cat.id}
                      onClick={() => setSelectedCategory(cat.id)}
                      className={`p-3 rounded-lg border text-left transition-colors ${
                        selectedCategory === cat.id
                          ? (practiceMode === 'interview' ? 'bg-emerald-500/20 border-emerald-500 text-white' : 'bg-cyan-500/20 border-cyan-500 text-white')
                          : 'bg-white/5 border-white/10 text-slate-300 hover:bg-white/10'
                      }`}
                      data-testid={`category-${cat.id}`}
                    >
                      <p className="font-medium">{cat.name}</p>
                      <p className="text-xs text-slate-400">{cat.description}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Persona Selection */}
              <div>
                <label className="text-sm font-medium text-slate-300 mb-3 block">
                  2. {practiceMode === 'interview' ? 'Select Who You\'re Calling' : 'Select Buyer Persona'}
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {personas.map((persona) => (
                    <button
                      key={persona.id}
                      onClick={() => setSelectedPersona(persona.id)}
                      className={`p-3 rounded-lg border text-left transition-colors ${
                        selectedPersona === persona.id
                          ? (practiceMode === 'interview' ? 'bg-emerald-500/20 border-emerald-500 text-white' : 'bg-cyan-500/20 border-cyan-500 text-white')
                          : 'bg-white/5 border-white/10 text-slate-300 hover:bg-white/10'
                      }`}
                      data-testid={`persona-${persona.id}`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <p className="font-medium">{persona.name}</p>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          persona.difficulty === 'Easy' ? 'bg-green-500/20 text-green-400' :
                          persona.difficulty === 'Medium' ? 'bg-amber-500/20 text-amber-400' :
                          'bg-red-500/20 text-red-400'
                        }`}>
                          {persona.difficulty}
                        </span>
                      </div>
                      <p className="text-xs text-slate-400">{persona.traits}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Scenario Selection */}
              <div>
                <label className="text-sm font-medium text-slate-300 mb-3 block">
                  3. {practiceMode === 'interview' ? 'Select Outreach Scenario' : 'Select Scenario'}
                </label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {scenarios.map((scenario) => (
                    <button
                      key={scenario.id}
                      onClick={() => setSelectedScenario(scenario.id)}
                      className={`p-3 rounded-lg border text-left transition-colors ${
                        selectedScenario === scenario.id
                          ? (practiceMode === 'interview' ? 'bg-emerald-500/20 border-emerald-500 text-white' : 'bg-cyan-500/20 border-cyan-500 text-white')
                          : 'bg-white/5 border-white/10 text-slate-300 hover:bg-white/10'
                      }`}
                      data-testid={`scenario-${scenario.id}`}
                    >
                      <p className="font-medium">{scenario.name}</p>
                      <p className="text-xs text-slate-400">{scenario.description}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Microphone Permission Note */}
              <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4">
                <p className="text-amber-400 text-sm flex items-center gap-2">
                  <Mic className="h-4 w-4" />
                  <strong>Microphone Required:</strong>
                </p>
                <p className="text-slate-300 text-sm mt-1">
                  {practiceMode === 'interview'
                    ? 'Speak naturally as you would when cold-calling for a job opportunity. The AI will respond as the selected persona.'
                    : 'Please allow microphone access when prompted. Speak naturally and the AI will respond as your selected persona.'}
                </p>
              </div>

              {/* Start Button */}
              <Button
                onClick={startSession}
                disabled={!selectedCategory || !selectedPersona || !selectedScenario}
                className={`w-full h-12 text-white ${
                  practiceMode === 'interview'
                    ? 'bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600'
                    : 'bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600'
                }`}
                data-testid="begin-session-btn"
              >
                <PhoneCall className="h-5 w-5 mr-2" />
                {practiceMode === 'interview' ? 'Start Interview Outreach Call' : 'Start Call'}
              </Button>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Results Modal - Enhanced */}
      {showResults && sessionResults && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-start justify-center z-50 p-4 overflow-y-auto">
          <Card className="w-full max-w-3xl bg-gradient-to-b from-slate-900 to-slate-950 border-white/10 relative my-auto max-h-[90vh] flex flex-col" style={{ marginTop: '5vh', marginBottom: '5vh' }}>
            {/* Fixed Close Button - Always visible */}
            <button
              onClick={() => setShowResults(false)}
              className="absolute top-3 right-3 text-slate-400 hover:text-white z-20 bg-slate-800 rounded-full p-2 hover:bg-slate-700 transition-colors shadow-lg border border-white/10"
              data-testid="close-results-btn"
            >
              <X className="h-6 w-6" />
            </button>
            
            {/* Header with animated gradient */}
            <CardHeader className="text-center border-b border-white/10 bg-gradient-to-r from-cyan-900/30 via-blue-900/30 to-purple-900/30 pb-8 pt-12 flex-shrink-0">
              <div className="mx-auto bg-gradient-to-r from-cyan-500 to-blue-500 w-20 h-20 rounded-full flex items-center justify-center mb-4 shadow-lg shadow-cyan-500/30">
                <BarChart3 className="h-10 w-10 text-white" />
              </div>
              <CardTitle className="text-white text-2xl mb-2">Call Performance Analysis</CardTitle>
              <p className="text-slate-400 text-sm">Here's how you did on this practice call</p>
            </CardHeader>
            
            <CardContent className="space-y-6 p-6 overflow-y-auto flex-1">
              {/* Overall Score with Gauge */}
              <div className="relative bg-gradient-to-br from-slate-800/50 to-slate-900/50 rounded-2xl p-8 border border-white/5">
                <div className="flex flex-col md:flex-row items-center justify-center gap-8">
                  {/* Score Circle */}
                  <div className="relative">
                    <svg className="w-40 h-40 transform -rotate-90">
                      <circle
                        cx="80"
                        cy="80"
                        r="70"
                        stroke="currentColor"
                        strokeWidth="12"
                        fill="none"
                        className="text-slate-700"
                      />
                      <circle
                        cx="80"
                        cy="80"
                        r="70"
                        stroke="url(#scoreGradient)"
                        strokeWidth="12"
                        fill="none"
                        strokeLinecap="round"
                        strokeDasharray={`${(sessionResults.analysis?.overall_score || 0) * 4.4} 440`}
                        className="transition-all duration-1000"
                      />
                      <defs>
                        <linearGradient id="scoreGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                          <stop offset="0%" stopColor="#06b6d4" />
                          <stop offset="100%" stopColor="#3b82f6" />
                        </linearGradient>
                      </defs>
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      <span className="text-5xl font-bold text-white" data-testid="session-score">
                        {sessionResults.analysis?.overall_score || 0}
                      </span>
                      <span className="text-slate-400 text-sm">out of 100</span>
                    </div>
                  </div>
                  
                  {/* Score interpretation */}
                  <div className="text-center md:text-left">
                    <div className={`text-2xl font-bold mb-2 ${
                      (sessionResults.analysis?.overall_score || 0) >= 80 ? 'text-green-400' :
                      (sessionResults.analysis?.overall_score || 0) >= 60 ? 'text-amber-400' :
                      (sessionResults.analysis?.overall_score || 0) >= 40 ? 'text-orange-400' : 'text-red-400'
                    }`}>
                      {(sessionResults.analysis?.overall_score || 0) >= 80 ? 'Excellent!' :
                       (sessionResults.analysis?.overall_score || 0) >= 60 ? 'Good Job!' :
                       (sessionResults.analysis?.overall_score || 0) >= 40 ? 'Keep Practicing' : 'Room for Growth'}
                    </div>
                    {sessionResults.analysis?.summary && (
                      <p className="text-slate-300 text-sm max-w-sm">{sessionResults.analysis.summary}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Detailed Scores - Enhanced Grid */}
              {sessionResults.analysis?.scores && (
                <div className="space-y-3">
                  <h3 className="text-white font-semibold flex items-center gap-2">
                    <TrendingUp className="h-4 w-4 text-cyan-400" />
                    Performance Breakdown
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {Object.entries(sessionResults.analysis.scores).map(([key, value]) => (
                      <div key={key} className="bg-slate-800/50 rounded-xl p-4 border border-slate-700/50">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-slate-400 text-xs capitalize font-medium">{key.replace(/_/g, ' ')}</span>
                          <span className={`text-lg font-bold ${
                            value >= 80 ? 'text-green-400' :
                            value >= 60 ? 'text-amber-400' : 'text-red-400'
                          }`}>{value}</span>
                        </div>
                        <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                          <div 
                            className={`h-full rounded-full transition-all duration-500 ${
                              value >= 80 ? 'bg-gradient-to-r from-green-500 to-emerald-400' :
                              value >= 60 ? 'bg-gradient-to-r from-amber-500 to-yellow-400' :
                              'bg-gradient-to-r from-red-500 to-orange-400'
                            }`}
                            style={{ width: `${value}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Strengths & Improvements Grid */}
              <div className="grid md:grid-cols-2 gap-4">
                {/* Strengths */}
                {sessionResults.analysis?.strengths?.length > 0 && (
                  <div className="bg-green-500/10 border border-green-500/20 rounded-xl p-5">
                    <h4 className="text-green-400 font-semibold mb-4 flex items-center gap-2">
                      <CheckCircle className="h-5 w-5" /> What You Did Well
                    </h4>
                    <ul className="space-y-3">
                      {sessionResults.analysis.strengths.map((s, i) => (
                        <li key={i} className="text-slate-300 text-sm flex items-start gap-3">
                          <span className="text-green-400 mt-0.5">✓</span>
                          <span>{s}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {/* Improvements */}
                {sessionResults.analysis?.improvements?.length > 0 && (
                  <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-5">
                    <h4 className="text-amber-400 font-semibold mb-4 flex items-center gap-2">
                      <Target className="h-5 w-5" /> Focus Areas
                    </h4>
                    <ul className="space-y-3">
                      {sessionResults.analysis.improvements.map((a, i) => (
                        <li key={i} className="text-slate-300 text-sm flex items-start gap-3">
                          <span className="text-amber-400 mt-0.5">→</span>
                          <span>{a}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              {/* Coaching Tips */}
              {sessionResults.analysis?.coaching_tips?.length > 0 && (
                <div className="bg-gradient-to-br from-cyan-500/10 to-blue-500/10 border border-cyan-500/20 rounded-xl p-5">
                  <h4 className="text-cyan-400 font-semibold mb-4 flex items-center gap-2">
                    <Lightbulb className="h-5 w-5" /> Pro Tips for Next Time
                  </h4>
                  <div className="space-y-3">
                    {sessionResults.analysis.coaching_tips.map((tip, i) => (
                      <div key={i} className="flex items-start gap-3 bg-slate-800/30 rounded-lg p-3">
                        <span className="bg-cyan-500 text-white text-xs font-bold w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0">
                          {i + 1}
                        </span>
                        <span className="text-slate-300 text-sm">{tip}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* New Achievements */}
              {sessionResults.new_achievements?.length > 0 && (
                <div className="bg-gradient-to-r from-amber-500/20 via-yellow-500/20 to-amber-500/20 border border-amber-500/30 rounded-xl p-5 text-center">
                  <div className="flex items-center justify-center gap-2 mb-4">
                    <Award className="h-6 w-6 text-amber-400" />
                    <h4 className="text-amber-400 font-bold text-lg">Achievement Unlocked!</h4>
                    <Award className="h-6 w-6 text-amber-400" />
                  </div>
                  <div className="flex flex-wrap justify-center gap-3">
                    {sessionResults.new_achievements.map((a, i) => (
                      <div key={i} className="bg-slate-800/50 rounded-lg px-4 py-2 flex items-center gap-2">
                        <Trophy className="h-5 w-5 text-amber-500" />
                        <span className="text-white font-medium">{a.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 pt-2">
                <Button
                  onClick={() => setShowResults(false)}
                  className="flex-1 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white font-semibold py-6"
                  data-testid="continue-training-btn"
                >
                  <PhoneCall className="h-5 w-5 mr-2" />
                  Practice Another Call
                </Button>
                <Button
                  variant="outline"
                  onClick={() => { setShowResults(false); setShowPrepCenter(true); }}
                  className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-800 py-6"
                >
                  <BookOpen className="h-5 w-5 mr-2" />
                  Review Scripts & Tips
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* History Modal */}
      {showHistory && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-2xl bg-slate-900 border-white/10 relative max-h-[80vh] overflow-hidden flex flex-col">
            <button
              onClick={() => setShowHistory(false)}
              className="absolute top-4 right-4 text-slate-400 hover:text-white z-10"
            >
              <X className="h-5 w-5" />
            </button>
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <History className="h-5 w-5 text-cyan-500" />
                Call History
              </CardTitle>
            </CardHeader>
            <CardContent className="overflow-y-auto flex-1">
              {callHistory.length > 0 ? (
                <div className="space-y-3">
                  {callHistory.map((call, idx) => (
                    <div key={idx} className="flex items-center justify-between bg-white/5 rounded-lg p-4">
                      <div>
                        <p className="text-white font-medium">{call.persona_name}</p>
                        <p className="text-slate-400 text-sm">{call.category_name} • {call.scenario_name}</p>
                        <p className="text-slate-500 text-xs mt-1">
                          {new Date(call.completed_at).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className={`font-bold text-lg ${
                          call.score >= 80 ? 'text-green-400' :
                          call.score >= 60 ? 'text-amber-400' : 'text-red-400'
                        }`}>
                          {call.score}
                        </p>
                        <p className="text-slate-400 text-sm">{formatTime(call.duration_seconds)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-400 text-center py-8">
                  No call history yet. Start practicing!
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Preparation Center Modal */}
      {showPrepCenter && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto">
          <Card className="w-full max-w-5xl bg-slate-900 border-white/10 relative my-8">
            <button
              onClick={() => {
                setShowPrepCenter(false);
                setSelectedScript(null);
                setSelectedPersonaGuide(null);
              }}
              className="absolute top-4 right-4 text-slate-400 hover:text-white z-10"
            >
              <X className="h-5 w-5" />
            </button>
            <CardHeader className="border-b border-white/10">
              <CardTitle className="text-white flex items-center gap-2">
                <BookOpen className="h-5 w-5 text-purple-500" />
                Preparation Center
              </CardTitle>
              <p className="text-slate-400 text-sm">Master these resources before your practice calls</p>
            </CardHeader>
            <CardContent className="p-0">
              {/* Tabs */}
              <div className="flex border-b border-white/10">
                <button
                  onClick={() => { setPrepTab('scripts'); setSelectedScript(null); setSelectedPersonaGuide(null); }}
                  className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                    prepTab === 'scripts' 
                      ? 'text-purple-400 border-b-2 border-purple-500 bg-purple-500/10' 
                      : 'text-slate-400 hover:text-white'
                  }`}
                >
                  <FileText className="h-4 w-4 inline mr-2" />
                  Call Scripts
                </button>
                <button
                  onClick={() => { setPrepTab('personas'); setSelectedScript(null); setSelectedPersonaGuide(null); }}
                  className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                    prepTab === 'personas' 
                      ? 'text-purple-400 border-b-2 border-purple-500 bg-purple-500/10' 
                      : 'text-slate-400 hover:text-white'
                  }`}
                >
                  <UserCheck className="h-4 w-4 inline mr-2" />
                  Persona Guides
                </button>
                <button
                  onClick={() => { setPrepTab('tips'); setSelectedScript(null); setSelectedPersonaGuide(null); }}
                  className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                    prepTab === 'tips' 
                      ? 'text-purple-400 border-b-2 border-purple-500 bg-purple-500/10' 
                      : 'text-slate-400 hover:text-white'
                  }`}
                >
                  <Lightbulb className="h-4 w-4 inline mr-2" />
                  Best Practices
                </button>
              </div>

              {/* Tab Content */}
              <div className="p-6 max-h-[60vh] overflow-y-auto">
                {/* Scripts Tab */}
                {prepTab === 'scripts' && !selectedScript && (
                  <div className="grid md:grid-cols-2 gap-4">
                    {Object.entries(CALL_SCRIPTS).map(([key, script]) => (
                      <button
                        key={key}
                        onClick={() => setSelectedScript(key)}
                        className="text-left bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg p-4 transition-colors"
                      >
                        <h4 className="text-white font-medium mb-1">{script.title}</h4>
                        <p className="text-slate-400 text-sm">{script.description}</p>
                      </button>
                    ))}
                  </div>
                )}

                {/* Selected Script View */}
                {prepTab === 'scripts' && selectedScript && (
                  <div>
                    <button 
                      onClick={() => setSelectedScript(null)}
                      className="text-purple-400 hover:text-purple-300 text-sm mb-4 flex items-center gap-1"
                    >
                      ← Back to Scripts
                    </button>
                    <h3 className="text-xl font-bold text-white mb-2">{CALL_SCRIPTS[selectedScript].title}</h3>
                    <p className="text-slate-400 mb-4">{CALL_SCRIPTS[selectedScript].description}</p>
                    
                    <div className="bg-slate-800/50 rounded-lg p-4 mb-4">
                      <h4 className="text-purple-400 font-medium mb-2">Script Template</h4>
                      <pre className="text-slate-300 text-sm whitespace-pre-wrap font-sans">
                        {CALL_SCRIPTS[selectedScript].template}
                      </pre>
                    </div>

                    <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4">
                      <h4 className="text-green-400 font-medium mb-2 flex items-center gap-2">
                        <Lightbulb className="h-4 w-4" /> Pro Tips
                      </h4>
                      <ul className="space-y-2">
                        {CALL_SCRIPTS[selectedScript].tips.map((tip, i) => (
                          <li key={i} className="text-slate-300 text-sm flex items-start gap-2">
                            <CheckCircle className="h-4 w-4 text-green-400 mt-0.5 flex-shrink-0" />
                            {tip}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}

                {/* Personas Tab */}
                {prepTab === 'personas' && !selectedPersonaGuide && (
                  <div className="grid md:grid-cols-2 gap-4">
                    {Object.entries(PERSONA_GUIDES).map(([key, persona]) => (
                      <button
                        key={key}
                        onClick={() => setSelectedPersonaGuide(key)}
                        className="text-left bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg p-4 transition-colors"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="text-white font-medium">{persona.name}</h4>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            persona.difficulty === 'Easy' ? 'bg-green-500/20 text-green-400' :
                            persona.difficulty === 'Medium' ? 'bg-amber-500/20 text-amber-400' :
                            'bg-red-500/20 text-red-400'
                          }`}>
                            {persona.difficulty}
                          </span>
                        </div>
                        <p className="text-slate-400 text-sm">Learn how to handle this persona type</p>
                      </button>
                    ))}
                  </div>
                )}

                {/* Selected Persona Guide View */}
                {prepTab === 'personas' && selectedPersonaGuide && (
                  <div>
                    <button 
                      onClick={() => setSelectedPersonaGuide(null)}
                      className="text-purple-400 hover:text-purple-300 text-sm mb-4 flex items-center gap-1"
                    >
                      ← Back to Personas
                    </button>
                    <div className="flex items-center gap-3 mb-4">
                      <h3 className="text-xl font-bold text-white">{PERSONA_GUIDES[selectedPersonaGuide].name}</h3>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        PERSONA_GUIDES[selectedPersonaGuide].difficulty === 'Easy' ? 'bg-green-500/20 text-green-400' :
                        PERSONA_GUIDES[selectedPersonaGuide].difficulty === 'Medium' ? 'bg-amber-500/20 text-amber-400' :
                        'bg-red-500/20 text-red-400'
                      }`}>
                        {PERSONA_GUIDES[selectedPersonaGuide].difficulty}
                      </span>
                    </div>
                    
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
                        <h4 className="text-blue-400 font-medium mb-2 flex items-center gap-2">
                          <Target className="h-4 w-4" /> What They Care About
                        </h4>
                        <ul className="space-y-2">
                          {PERSONA_GUIDES[selectedPersonaGuide].whatTheyCareAbout.map((item, i) => (
                            <li key={i} className="text-slate-300 text-sm flex items-start gap-2">
                              <span className="text-blue-400">•</span> {item}
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4">
                        <h4 className="text-amber-400 font-medium mb-2 flex items-center gap-2">
                          <MessageSquare className="h-4 w-4" /> How They Respond
                        </h4>
                        <ul className="space-y-2">
                          {PERSONA_GUIDES[selectedPersonaGuide].howTheyRespond.map((item, i) => (
                            <li key={i} className="text-slate-300 text-sm flex items-start gap-2">
                              <span className="text-amber-400">•</span> {item}
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4">
                        <h4 className="text-green-400 font-medium mb-2 flex items-center gap-2">
                          <CheckCircle className="h-4 w-4" /> How to Win Them
                        </h4>
                        <ul className="space-y-2">
                          {PERSONA_GUIDES[selectedPersonaGuide].howToWinThem.map((item, i) => (
                            <li key={i} className="text-slate-300 text-sm flex items-start gap-2">
                              <span className="text-green-400">•</span> {item}
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
                        <h4 className="text-red-400 font-medium mb-2 flex items-center gap-2">
                          <Shield className="h-4 w-4" /> Common Objections
                        </h4>
                        <ul className="space-y-2">
                          {PERSONA_GUIDES[selectedPersonaGuide].commonObjections.map((item, i) => (
                            <li key={i} className="text-slate-300 text-sm flex items-start gap-2">
                              <span className="text-red-400">"</span> {item}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                )}

                {/* Best Practices Tab */}
                {prepTab === 'tips' && (
                  <div className="space-y-4">
                    {BEST_PRACTICES.map((section, idx) => (
                      <div key={idx} className="bg-white/5 border border-white/10 rounded-lg overflow-hidden">
                        <button
                          onClick={() => setExpandedTips(prev => ({ ...prev, [idx]: !prev[idx] }))}
                          className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-white/5 transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            <span className="text-purple-400">{getPracticeIcon(section.icon)}</span>
                            <h4 className="text-white font-medium">{section.title}</h4>
                          </div>
                          {expandedTips[idx] ? (
                            <ChevronUp className="h-5 w-5 text-slate-400" />
                          ) : (
                            <ChevronDown className="h-5 w-5 text-slate-400" />
                          )}
                        </button>
                        {expandedTips[idx] && (
                          <div className="px-4 pb-4">
                            <ul className="space-y-2 ml-10">
                              {section.tips.map((tip, i) => (
                                <li key={i} className="text-slate-300 text-sm flex items-start gap-2">
                                  <CheckCircle className="h-4 w-4 text-green-400 mt-0.5 flex-shrink-0" />
                                  {tip}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      {/* Interview Prep Center Modal */}
      {showInterviewPrepCenter && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto">
          <Card className="w-full max-w-5xl bg-slate-900 border-white/10 relative my-8">
            <button
              onClick={() => {
                setShowInterviewPrepCenter(false);
                setSelectedInterviewScript(null);
                setSelectedInterviewPersonaGuide(null);
              }}
              className="absolute top-4 right-4 text-slate-400 hover:text-white z-10"
              data-testid="close-interview-prep-btn"
            >
              <X className="h-5 w-5" />
            </button>
            <CardHeader className="border-b border-white/10">
              <CardTitle className="text-white flex items-center gap-2">
                <Briefcase className="h-5 w-5 text-emerald-500" />
                Interview Prep Center
              </CardTitle>
              <p className="text-slate-400 text-sm">Master these resources before your interview outreach calls</p>
            </CardHeader>
            <CardContent className="p-0">
              {/* Tabs */}
              <div className="flex border-b border-white/10">
                <button
                  onClick={() => { setInterviewPrepTab('scripts'); setSelectedInterviewScript(null); setSelectedInterviewPersonaGuide(null); }}
                  className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                    interviewPrepTab === 'scripts' 
                      ? 'text-emerald-400 border-b-2 border-emerald-500 bg-emerald-500/10' 
                      : 'text-slate-400 hover:text-white'
                  }`}
                  data-testid="interview-prep-scripts-tab"
                >
                  <FileText className="h-4 w-4 inline mr-2" />
                  Outreach Scripts
                </button>
                <button
                  onClick={() => { setInterviewPrepTab('personas'); setSelectedInterviewScript(null); setSelectedInterviewPersonaGuide(null); }}
                  className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                    interviewPrepTab === 'personas' 
                      ? 'text-emerald-400 border-b-2 border-emerald-500 bg-emerald-500/10' 
                      : 'text-slate-400 hover:text-white'
                  }`}
                  data-testid="interview-prep-personas-tab"
                >
                  <UserCheck className="h-4 w-4 inline mr-2" />
                  Persona Playbooks
                </button>
                <button
                  onClick={() => { setInterviewPrepTab('tips'); setSelectedInterviewScript(null); setSelectedInterviewPersonaGuide(null); }}
                  className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                    interviewPrepTab === 'tips' 
                      ? 'text-emerald-400 border-b-2 border-emerald-500 bg-emerald-500/10' 
                      : 'text-slate-400 hover:text-white'
                  }`}
                  data-testid="interview-prep-tips-tab"
                >
                  <Lightbulb className="h-4 w-4 inline mr-2" />
                  Best Practices
                </button>
              </div>

              {/* Tab Content */}
              <div className="p-6 max-h-[60vh] overflow-y-auto">
                {/* Interview Scripts Tab */}
                {interviewPrepTab === 'scripts' && !selectedInterviewScript && (
                  <div className="grid md:grid-cols-2 gap-4">
                    {Object.entries(INTERVIEW_SCRIPTS).map(([key, script]) => (
                      <button
                        key={key}
                        onClick={() => setSelectedInterviewScript(key)}
                        className="text-left bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg p-4 transition-colors"
                        data-testid={`interview-script-${key}`}
                      >
                        <h4 className="text-white font-medium mb-1">{script.title}</h4>
                        <p className="text-slate-400 text-sm">{script.description}</p>
                      </button>
                    ))}
                  </div>
                )}

                {/* Selected Interview Script View */}
                {interviewPrepTab === 'scripts' && selectedInterviewScript && (
                  <div>
                    <button 
                      onClick={() => setSelectedInterviewScript(null)}
                      className="text-emerald-400 hover:text-emerald-300 text-sm mb-4 flex items-center gap-1"
                    >
                      <ArrowRight className="h-3 w-3 rotate-180" /> Back to Scripts
                    </button>
                    <h3 className="text-xl font-bold text-white mb-2">{INTERVIEW_SCRIPTS[selectedInterviewScript].title}</h3>
                    <p className="text-slate-400 mb-4">{INTERVIEW_SCRIPTS[selectedInterviewScript].description}</p>
                    
                    <div className="bg-slate-800/50 rounded-lg p-4 mb-4">
                      <h4 className="text-emerald-400 font-medium mb-2">Script Template</h4>
                      <pre className="text-slate-300 text-sm whitespace-pre-wrap font-sans">
                        {INTERVIEW_SCRIPTS[selectedInterviewScript].template}
                      </pre>
                    </div>

                    <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4">
                      <h4 className="text-green-400 font-medium mb-2 flex items-center gap-2">
                        <Lightbulb className="h-4 w-4" /> Pro Tips
                      </h4>
                      <ul className="space-y-2">
                        {INTERVIEW_SCRIPTS[selectedInterviewScript].tips.map((tip, i) => (
                          <li key={i} className="text-slate-300 text-sm flex items-start gap-2">
                            <CheckCircle className="h-4 w-4 text-green-400 mt-0.5 flex-shrink-0" />
                            {tip}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}

                {/* Interview Personas Tab */}
                {interviewPrepTab === 'personas' && !selectedInterviewPersonaGuide && (
                  <div className="grid md:grid-cols-2 gap-4">
                    {Object.entries(INTERVIEW_PERSONA_GUIDES).map(([key, persona]) => (
                      <button
                        key={key}
                        onClick={() => setSelectedInterviewPersonaGuide(key)}
                        className="text-left bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg p-4 transition-colors"
                        data-testid={`interview-persona-${key}`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="text-white font-medium">{persona.name}</h4>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            persona.difficulty === 'Easy' ? 'bg-green-500/20 text-green-400' :
                            persona.difficulty === 'Medium' ? 'bg-amber-500/20 text-amber-400' :
                            'bg-red-500/20 text-red-400'
                          }`}>
                            {persona.difficulty}
                          </span>
                        </div>
                        <p className="text-slate-400 text-sm">Learn how to approach this decision-maker</p>
                      </button>
                    ))}
                  </div>
                )}

                {/* Selected Interview Persona Guide View */}
                {interviewPrepTab === 'personas' && selectedInterviewPersonaGuide && (
                  <div>
                    <button 
                      onClick={() => setSelectedInterviewPersonaGuide(null)}
                      className="text-emerald-400 hover:text-emerald-300 text-sm mb-4 flex items-center gap-1"
                    >
                      <ArrowRight className="h-3 w-3 rotate-180" /> Back to Personas
                    </button>
                    <div className="flex items-center gap-3 mb-4">
                      <h3 className="text-xl font-bold text-white">{INTERVIEW_PERSONA_GUIDES[selectedInterviewPersonaGuide].name}</h3>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        INTERVIEW_PERSONA_GUIDES[selectedInterviewPersonaGuide].difficulty === 'Easy' ? 'bg-green-500/20 text-green-400' :
                        INTERVIEW_PERSONA_GUIDES[selectedInterviewPersonaGuide].difficulty === 'Medium' ? 'bg-amber-500/20 text-amber-400' :
                        'bg-red-500/20 text-red-400'
                      }`}>
                        {INTERVIEW_PERSONA_GUIDES[selectedInterviewPersonaGuide].difficulty}
                      </span>
                    </div>
                    
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-4">
                        <h4 className="text-emerald-400 font-medium mb-2 flex items-center gap-2">
                          <Target className="h-4 w-4" /> What They Care About
                        </h4>
                        <ul className="space-y-2">
                          {INTERVIEW_PERSONA_GUIDES[selectedInterviewPersonaGuide].whatTheyCareAbout.map((item, i) => (
                            <li key={i} className="text-slate-300 text-sm flex items-start gap-2">
                              <span className="text-emerald-400">-</span> {item}
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4">
                        <h4 className="text-amber-400 font-medium mb-2 flex items-center gap-2">
                          <MessageSquare className="h-4 w-4" /> How They Respond
                        </h4>
                        <ul className="space-y-2">
                          {INTERVIEW_PERSONA_GUIDES[selectedInterviewPersonaGuide].howTheyRespond.map((item, i) => (
                            <li key={i} className="text-slate-300 text-sm flex items-start gap-2">
                              <span className="text-amber-400">-</span> {item}
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4">
                        <h4 className="text-green-400 font-medium mb-2 flex items-center gap-2">
                          <CheckCircle className="h-4 w-4" /> How to Win Them
                        </h4>
                        <ul className="space-y-2">
                          {INTERVIEW_PERSONA_GUIDES[selectedInterviewPersonaGuide].howToWinThem.map((item, i) => (
                            <li key={i} className="text-slate-300 text-sm flex items-start gap-2">
                              <span className="text-green-400">-</span> {item}
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
                        <h4 className="text-red-400 font-medium mb-2 flex items-center gap-2">
                          <Shield className="h-4 w-4" /> Common Objections
                        </h4>
                        <ul className="space-y-2">
                          {INTERVIEW_PERSONA_GUIDES[selectedInterviewPersonaGuide].commonObjections.map((item, i) => (
                            <li key={i} className="text-slate-300 text-sm flex items-start gap-2">
                              <span className="text-red-400">"</span> {item}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                )}

                {/* Interview Best Practices Tab */}
                {interviewPrepTab === 'tips' && (
                  <div className="space-y-4">
                    {INTERVIEW_BEST_PRACTICES.map((section, idx) => (
                      <div key={idx} className="bg-white/5 border border-white/10 rounded-lg overflow-hidden">
                        <button
                          onClick={() => setExpandedInterviewTips(prev => ({ ...prev, [idx]: !prev[idx] }))}
                          className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-white/5 transition-colors"
                          data-testid={`interview-tip-${idx}`}
                        >
                          <div className="flex items-center gap-3">
                            <span className="text-emerald-400">{getPracticeIcon(section.icon)}</span>
                            <h4 className="text-white font-medium">{section.title}</h4>
                          </div>
                          {expandedInterviewTips[idx] ? (
                            <ChevronUp className="h-5 w-5 text-slate-400" />
                          ) : (
                            <ChevronDown className="h-5 w-5 text-slate-400" />
                          )}
                        </button>
                        {expandedInterviewTips[idx] && (
                          <div className="px-4 pb-4">
                            <ul className="space-y-2 ml-10">
                              {section.tips.map((tip, i) => (
                                <li key={i} className="text-slate-300 text-sm flex items-start gap-2">
                                  <CheckCircle className="h-4 w-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                                  {tip}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
